import { redirect } from 'react-router';

import {
  createPayoutAccountURLByUserAndType,
  createPayoutLoginURLByUser,
  payoutAccountByUserID,
} from '@/actions/payoutDashboard';
import config from '@/config';
import { ERROR_CODES, type ErrorCode, hasErrorCode } from '@/core/api';
import NotFoundPage from '@/pages/NotFoundPage';

import { LegacyRoute } from '../routesMiddleware';

/**
 * Coded onboarding failures, and where each sends the expert.
 *
 * Order is presentational only: matching is exact code equality, one error per
 * failure on this path, so a row can only ever match its own code. Do not read
 * a precedence rule into the sequence.
 *
 * `country_required` is the only one that routes to a form rather than the
 * settings index: it is the one an expert can fix themselves, and the default
 * state of every new expert, since nothing but the profile Location form ever
 * writes a country.
 */
const PAYOUT_SETUP_ERROR_ROUTES: readonly (readonly [ErrorCode, string])[] = [
  [ERROR_CODES.PAYOUT_COUNTRY_REQUIRED, '/settings/location?error=country_required'],
  [ERROR_CODES.PAYOUT_COUNTRY_UNSUPPORTED, '/settings?error=country_unsupported'],
  [ERROR_CODES.PAYOUT_SETUP_REJECTED, '/settings?error=payout_setup_rejected'],
  [ERROR_CODES.PAYOUT_SETUP_RETRY_LATER, '/settings?error=payout_setup_retry_later'],
  [ERROR_CODES.PAYOUT_SETUP_FAILED, '/settings?error=payout_setup_failed'],
];

const route: LegacyRoute = {
  //subdomain: '*',
  path: '/settings/payout',

  async action({ store }) {
    if (!config.enablePayoutDashboard) {
      return <NotFoundPage />;
    }

    const { viewer } = store.getState();
    const userId = viewer.id;
    let payoutsEnabled = false;

    // ENG-5722. An expert who has never onboarded has no `payout_accounts`
    // row, and the api reports that as the coded `payout.account_not_found`
    // rather than a null row (`payout/payout_service.go`). That is the normal
    // starting state of every expert, not a failure: the onboarding branch
    // below is what creates the account, because `CreateAccountURL` calls
    // `ensureAccount`, which writes the row and provisions the Stripe Express
    // account when there is none. Returning early here short-circuited it and
    // dead-ended the one self-serve path to Stripe Connect on "contact
    // support". So leave `payoutsEnabled` false and fall through.
    //
    // `hasErrorCode`, not `hasOnlyErrorCode` as MakeOfferDrawer uses for the
    // same code: that helper answers only for `ApolloError`, and this route's
    // legacy thunks reject with `APIError`, so it would never match here.
    try {
      const account = await store.dispatch(payoutAccountByUserID(userId));
      payoutsEnabled = account.payouts_enabled;
    } catch (err) {
      if (!hasErrorCode(err, ERROR_CODES.PAYOUT_ACCOUNT_NOT_FOUND)) {
        throw err;
      }
    }

    let redirectUrl;
    try {
      if (payoutsEnabled) {
        redirectUrl = await store.dispatch(createPayoutLoginURLByUser(userId));
      } else {
        redirectUrl = await store.dispatch(
          createPayoutAccountURLByUserAndType(userId, 'account_onboarding')
        );
      }
    } catch (err) {
      // ENG-5723 gave the api four more codes for this call, all previously
      // uncoded and so all landing in the rethrow below as a generic error
      // page. Each one has copy the expert can act on, so each gets routed
      // instead — country_required to the form that fixes it, the rest to the
      // settings toast.
      // `hasErrorCode`, not `hasOnlyErrorCode`, and deliberately: this is a
      // legacy redux route, so `store.dispatch` rejects with an `APIError`,
      // and `hasOnlyErrorCode` returns false for anything that is not an
      // `ApolloError`. Swapping it in would make `target` always undefined and
      // send every payout failure to a generic error page. The any-match
      // caveat in `core/api.ts` still applies — closing it needs an
      // APIError-aware all-match helper, which is its own change.
      const target = PAYOUT_SETUP_ERROR_ROUTES.find(([code]) => hasErrorCode(err, code));
      if (target === undefined) {
        throw err;
      }
      redirectUrl = target[1];
    }

    return redirect(redirectUrl);
  },
};

export default route;
